import {
  DIMENSION_LABELS,
  DIMENSION_WEIGHTS,
  MATURITY_SCORE,
  type DimensionId,
  type MaturityValue,
  bandForScore,
} from '../../config/brsrConfig'
import { BRSR_QUESTIONS } from '../../data/brsr/questions'
import { NGRBC_PRINCIPLES } from '../../data/brsr/regulatorySources'
import type { AnswerMap, BrsrQuestion, DimensionResult, NamedScore } from '../../data/brsr/types'
import { isQuestionVisible } from './visibility'

const DIMENSION_ORDER: DimensionId[] = [
  'disclosure',
  'environmental',
  'social_governance',
  'evidence',
  'brsr_core',
  'value_chain',
]

export interface ScoreAccumulator {
  weighted: number
  weight: number
  answered: number
  applicable: number
  unknown: number
  unanswered: number
  excluded: number
  visible: number
}

export function emptyAccumulator(): ScoreAccumulator {
  return {
    weighted: 0,
    weight: 0,
    answered: 0,
    applicable: 0,
    unknown: 0,
    unanswered: 0,
    excluded: 0,
    visible: 0,
  }
}

function maturityScore(value: string): number | null | undefined {
  if (!(value in MATURITY_SCORE)) return undefined
  return MATURITY_SCORE[value as MaturityValue]
}

/** Questions that can affect a readiness score. Optional blanks are ignored. */
export function questionInScore(question: BrsrQuestion, responses: AnswerMap): boolean {
  if (!question.dimension) return false
  if (!isQuestionVisible(question, responses)) return false
  const value = responses[question.id]
  const answered = Array.isArray(value) ? value.length > 0 : typeof value === 'string' && value !== ''
  if (!question.required && !answered) return false
  return true
}

export function accumulateQuestion(acc: ScoreAccumulator, question: BrsrQuestion, responses: AnswerMap): void {
  if (!questionInScore(question, responses)) return
  acc.visible += 1
  const value = responses[question.id]
  const scalar = typeof value === 'string' ? value : ''
  if (!scalar) {
    acc.unanswered += 1
    acc.applicable += 1
    return
  }
  if (scalar === 'not_applicable') {
    acc.excluded += 1
    return
  }
  if (scalar === 'not_sure') {
    acc.unknown += 1
    acc.applicable += 1
    return
  }
  const score = maturityScore(scalar)
  if (score == null) {
    acc.unknown += 1
    acc.applicable += 1
    return
  }
  const weight = question.weight ?? 1
  acc.weighted += score * weight
  acc.weight += weight
  acc.answered += 1
  acc.applicable += 1
}

export function scoreFromAccumulator(acc: ScoreAccumulator): number | null {
  if (acc.weight <= 0) return null
  return Math.round(acc.weighted / acc.weight)
}

export function coverageFromAccumulator(acc: ScoreAccumulator): number {
  if (acc.applicable <= 0) return 0
  return Math.round((acc.answered / acc.applicable) * 100)
}

function pathwayFor(acc: ScoreAccumulator, visibleCount: number): DimensionResult['pathway'] {
  if (visibleCount === 0) return 'not_in_pathway'
  if (acc.applicable === 0 || acc.answered === 0) return 'insufficient'
  return 'scored'
}

export function scoreQuestionSet(questions: BrsrQuestion[], responses: AnswerMap): ScoreAccumulator {
  const acc = emptyAccumulator()
  for (const question of questions) accumulateQuestion(acc, question, responses)
  return acc
}

export function toNamedScore(id: string, label: string, questions: BrsrQuestion[], responses: AnswerMap): NamedScore {
  const visibleCount = questions.filter((question) => question.dimension && isQuestionVisible(question, responses)).length
  const acc = scoreQuestionSet(questions, responses)
  const pathway = pathwayFor(acc, visibleCount)
  const score = pathway === 'scored' ? scoreFromAccumulator(acc) : null
  return { id, label, score, coverage: coverageFromAccumulator(acc), pathway }
}

export function scoreDimensions(responses: AnswerMap): DimensionResult[] {
  return DIMENSION_ORDER.map((id) => {
    const questions = BRSR_QUESTIONS.filter((question) => question.dimension === id)
    const visibleCount = questions.filter((question) => isQuestionVisible(question, responses)).length
    const acc = scoreQuestionSet(questions, responses)
    const pathway = pathwayFor(acc, visibleCount)
    const score = pathway === 'scored' ? scoreFromAccumulator(acc) : null
    return {
      id,
      label: DIMENSION_LABELS[id],
      score,
      bandLabel: score == null ? null : bandForScore(score).label,
      coverage: coverageFromAccumulator(acc),
      answered: acc.answered,
      applicable: acc.applicable,
      unknown: acc.unknown,
      unanswered: acc.unanswered,
      excluded: acc.excluded,
      pathway,
    }
  })
}

export function overallFromDimensions(dimensions: DimensionResult[]): number | null {
  let weighted = 0
  let weight = 0
  for (const dimension of dimensions) {
    if (dimension.score == null || dimension.pathway !== 'scored') continue
    const dimensionWeight = DIMENSION_WEIGHTS[dimension.id]
    weighted += dimension.score * dimensionWeight
    weight += dimensionWeight
  }
  if (weight <= 0) return null
  return Math.round(weighted / weight)
}

export function completionStats(responses: AnswerMap): { completion: number; unanswered: number; unknown: number } {
  const requiredVisible = BRSR_QUESTIONS.filter((question) => question.required && isQuestionVisible(question, responses))
  let answered = 0
  let unknown = 0
  for (const question of requiredVisible) {
    const value = responses[question.id]
    const scalar = Array.isArray(value) ? value.length > 0 : typeof value === 'string' && value !== ''
    if (!scalar) continue
    answered += 1
    if (value === 'not_sure') unknown += 1
  }
  const unanswered = requiredVisible.length - answered
  const completion = requiredVisible.length === 0 ? 0 : Math.round((answered / requiredVisible.length) * 100)
  return { completion, unanswered, unknown }
}

export function isProvisional(dimensions: DimensionResult[], stats: { unanswered: number; unknown: number }, requiredVisible: number): boolean {
  if (stats.unanswered > 0) return true
  const critical = dimensions.filter((dimension) =>
    dimension.id === 'disclosure' || dimension.id === 'environmental' || dimension.id === 'evidence',
  )
  if (critical.some((dimension) => dimension.pathway === 'scored' && dimension.coverage < 70)) return true
  if (requiredVisible > 0 && stats.unknown / requiredVisible > 0.34) return true
  return false
}

export function scorePrinciples(responses: AnswerMap): NamedScore[] {
  return NGRBC_PRINCIPLES.map((principle) =>
    toNamedScore(
      principle.id,
      `${principle.id} — ${principle.title}`,
      BRSR_QUESTIONS.filter((question) => question.principle === principle.id),
      responses,
    ),
  )
}

export function scoreSections(responses: AnswerMap): NamedScore[] {
  const groups = [
    { id: 'A', label: 'Section A — General disclosures', match: (question: BrsrQuestion) => question.section.startsWith('Section A') },
    { id: 'B', label: 'Section B — Management and process disclosures', match: (question: BrsrQuestion) => question.section.startsWith('Section B') },
    { id: 'C', label: 'Section C — Principle-wise disclosures', match: (question: BrsrQuestion) => question.section.startsWith('Section C') },
  ]
  return groups.map((group) => toNamedScore(group.id, group.label, BRSR_QUESTIONS.filter(group.match), responses))
}
